import * as Sentry from '@sentry/react';
import { AxiosError, AxiosResponse } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getAuthToken } from 'src/utils/auth';
import axiosInstance, { maskSensitiveData } from 'src/utils/axios';
import { safeStringify } from 'src/utils/safe-stringify';

//
// captureException: can accepts custom tags and extra data

// Types for API error responses
interface ApiErrorResponse {
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

//
//

export const getRequestInterceptor = () => {
  return axiosInstance.interceptors.request.use(
    async (config: any) => {
      const token = await getAuthToken();
      let newHeaders = config.headers || {};
      if (token) {
        newHeaders = {
          ...newHeaders,
          Authorization: `Bearer ${token}`,
        };
      }

      if (config.url?.includes('customers/users/v1') && Sentry) {
        Sentry.addBreadcrumb({
          category: 'sentry.event',
          message: `Token len: ${token?.length} . Ath head len: ${
            String(newHeaders.Authorization)?.length
          }`,
          level: 'info',
        });
      }

      // Add timing metadata
      config.metadata = { startTime: new Date() };

      return { ...config, headers: newHeaders };
    },
    (error: AxiosError) => {
      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'request_error');
        scope.setExtra('request_error', {
          config: error.config,
          message: error.message,
          stack: error.stack,
        });
        Sentry.captureException(error);
      });

      enqueueSnackbar('Request failed', {
        variant: 'error',
        preventDuplicate: true,
      });
      return Promise.reject(error);
    }
  );
};

//

export const getResponseInterceptor = () => {
  return axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: Record<string, any>) => {
      console.log('error', error);

      const startTime = error.config?.metadata?.startTime;
      const duration = startTime ? new Date().getTime() - startTime.getTime() : null;

      if (error.response) {
        console.error({
          message: error.message,
          headers: JSON.stringify({
            apiMaskedKey: maskSensitiveData(error.config?.headers['x-api-key'], 2, 3),
            accept: error.config?.headers?.Accept,
            contentType: error.config?.headers['Content-Type'],
            maskedBearer: maskSensitiveData(error.config?.headers?.Authorization, 9, 3),
          }),
          response: JSON.stringify(error.response?.__sentry_xhr__),
          request: JSON.stringify(error.request?.__sentry_xhr__),
        });

        const message = error.response.data?.message || error.response.data?.errors?.[0].message || error?.message || 'An error occurred';

        const isCms404 =
          error?.response?.data?.errors?.some((e: ApiErrorResponse) => e.code === '404') &&
          error?.config?.url?.startsWith('/cms/');
        const isExpectedCustomerNotFound =
          error?.response?.status === 404 &&
          error?.config?.url?.startsWith('/customers/users/v1?email=');

        if (isCms404) {
          console.warn(
            'Not capturing this api error as exception because it is normal for CMS to respond with 404'
          );
        } else if (isExpectedCustomerNotFound) {
          console.warn(
            'Not capturing this api error as exception because it is normal for Not found customer to respond with 404'
          );
        } else {
          Sentry.withScope((scope) => {
            scope.setTag('error_type', 'api_error');
            scope.setTag('status_code', error.response?.status.toString());
            scope.setTag('endpoint', error.config?.url || 'unknown');

            scope.setExtra('error_context', {
              request: {
                url: error.config?.url,
                method: error.config?.method,
                headers: error.config?.headers?.toString() || 'undefined',
                data: error.config?.data?.toString() || 'undefined',
              },
              response: {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: safeStringify(error.response?.data) || 'undefined',
              },
              duration,
              timestamp: new Date().toISOString(),
            });

            if (error.response?.status === 422) {
              scope.setExtra('validation_errors', error.response?.data?.errors);
            }

            Sentry.captureException(error);
          });

          enqueueSnackbar(message, {
            variant: 'error',
            preventDuplicate: true,
          });
        }
      } else if (error.request) {
        Sentry.withScope((scope) => {
          scope.setTag('error_type', 'network_error');
          scope.setExtra('network_error_context', {
            request: safeStringify(error.request),
            config: error.config,
            online: navigator.onLine,
          });
          Sentry.captureException(new Error('Network Error'));
        });

        enqueueSnackbar('Network error. Please check your connection.', {
          variant: 'error',
          preventDuplicate: true,
        });
      }

      return Promise.reject(error);
    }
  );
};
